import dialogsNames from 'utils/constants/dialogs/dialogsNames';
import { guestHaveMoreThanOneBooking } from 'utils/booking';
import { setBookingsDirty, updateBookingSession } from 'utils/bookingSession';
import { hide, showGuestSuccess, showSuccess, showGuestResult } from 'state/modules/dialog';
import { clearProfile, clearTemplates } from 'state/modules/guest';
import { deleteReloadBookingCookie } from 'utils/cookies/deleteCookie';
import { checkPassportVerificationStatus } from 'utils/passportVerification';
import { passportVerificationStatus } from 'appConfig';
import { loadBookings } from './actions';
import TYPES from './types';
import { showGuestError, showError } from '../dialog/actions';

/**
 * Checks if the current route is the home page or bookings list page
 * Should show popup on:
 * - /:locale (home page, e.g., /en)
 * - /:locale/bookings (bookings list, e.g., /en/bookings)
 * Should NOT show on booking detail pages:
 * - /:locale/bookings/:id (e.g., /en/bookings/327507)
 * - /:locale/bookings/:id/* (e.g., /en/bookings/327507/financials)
 * @returns {boolean} True if on home or bookings list page
 */
const isOnHomeOrBookingListPage = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const { pathname } = window.location;
  // Normalize pathname by removing trailing slash
  const normalizedPathname = pathname.replace(/\/$/, '');
  // Match patterns:
  // - /:locale (exact match with locale only, e.g., /en or /en/)
  // - /:locale/bookings (exact match, e.g., /en/bookings or /en/bookings/)
  // Note: Does NOT match /:locale/bookings/:id or deeper paths
  const homePageRegex = /^\/[^/]+$/;
  const bookingsListRegex = /^\/[^/]+\/bookings$/;

  return homePageRegex.test(normalizedPathname) || bookingsListRegex.test(normalizedPathname);
};

const {
  ADD_GUEST_SUCCESS,
  LOAD_SUCCESS,
  LOAD_SUCCESS_SESSION_STORAGE,
  REMOVE_BOOKER_SUCCESS,
  REMOVE_GUEST_SUCCESS,
  UPDATE_GUEST_SUCCESS,
  UPDATE_GUEST_FAIL,
} = TYPES;

export default ({ dispatch, getState }) => next => action => {
  const { result, type, successMessage } = action;

  switch (type) {
    case ADD_GUEST_SUCCESS: {
      dispatch(hide(dialogsNames.GUEST_FORM));
      dispatch(hide(dialogsNames.GUEST_EXISTING));
      dispatch(showGuestResult({ messageId: 'dialog.message.add.guest.success', validation: result?.validation }));
      dispatch(clearTemplates());
      break;
    }
    case LOAD_SUCCESS:
    case LOAD_SUCCESS_SESSION_STORAGE: {
      updateBookingSession(result);
      deleteReloadBookingCookie();

      if (result?.upcoming) {
        const state = getState();
        const profile = state.auth?.user;

        if (profile) {
          const verificationIssue = checkPassportVerificationStatus(result.upcoming, profile);
          // Only show popup for FAILED passport verification and only on home or bookings list page
          if (
            verificationIssue &&
            verificationIssue.status === passportVerificationStatus.VERIFIED_FAILED &&
            isOnHomeOrBookingListPage()
          ) {
            const { tenant } = state;
            const email = tenant?.email || '';

            dispatch(showError({
              title: 'dialog.message.uploadPassport.verifiedFailed',
              message: 'dialog.message.uploadPassport.verifiedFailed.error',
              values: { email },
            }));
          }
        }
      }
      break;
    }
    case REMOVE_BOOKER_SUCCESS: {
      setBookingsDirty();
      dispatch(loadBookings());
      dispatch(showSuccess(successMessage));
      break;
    }
    case REMOVE_GUEST_SUCCESS: {
      dispatch(hide(dialogsNames.GUEST_FORM));
      dispatch(showGuestSuccess({ messageId: 'dialog.message.remove.guest.success', validation: result?.validation }));
      break;
    }
    case UPDATE_GUEST_SUCCESS: {
      const { booking: bookingState, guest: guestState } = getState();
      const upcomingBookings = bookingState?.bookings?.upcoming ?? [];
      const updatedGuestId = result?.cmsFolioGuest?.id;
      const profileGuestIds = guestState?.profile?.cmsGuestIds ?? [];

      // Reload the bookings if the updated guest in more than 1 upcoming bookings.
      if (guestHaveMoreThanOneBooking(updatedGuestId, upcomingBookings)) {
        setBookingsDirty();
        dispatch(loadBookings());
      }
      // Reset the profile data when the updated Guest ID is related to the profile
      const isProfileTemplateUpdate = profileGuestIds.includes(updatedGuestId);
      if (isProfileTemplateUpdate) {
        dispatch(clearProfile());
      }
      // Display success and clear the guest templates
      dispatch(hide(dialogsNames.GUEST_FORM));
      dispatch(clearTemplates());

      // Delay the success dialog to let the booking state update settle
      setTimeout(() => {
        dispatch(showGuestResult({
          messageId: 'dialog.message.edit.guest.success',
          validation: result?.validation,
        }));
      }, 0);
      break;
    }
    case UPDATE_GUEST_FAIL: {
      let error = '';
      try {
        error = JSON.parse(action.error?.message).message.join(', ');
      }
      catch {
        error = action.error?.message ?? '';
      }
      dispatch(showGuestError({ title: 'dialog.message.edit.guest.fail', message: error }));
      break;
    }
    default:
      break;
  }
  next(action);
};
